/** The reader's JSON API, as `prv.web.routes.quotation_reader` speaks it.
 * Money is a string of cents on both sides; nothing here does arithmetic, and
 * it is shown with `api/money`'s `formatMoney` like every other figure -- the
 * reader's own copy parsed it as a float. */

export type Kind = "part" | "labour" | "travel" | "unmatched";
export type SavedKind = "part" | "labour" | "travel" | "sundry";
export type Severity = "blocking" | "warning";

export interface CataloguePart {
  spare_part_id: number | null;
  code: string;
  description: string;
  size: string;
  unit_cost: string | null;
}

export interface ReadLine {
  seq: number;
  kind: Kind;
  description: string;
  size: string;
  qty: string;
  unit_cost: string | null;
  quoted_unit_cost: string | null;
  quoted_unit_price: string | null;
  price_matches: boolean;
  source_description: string;
  /** The item code the supplier printed on the line, if any. */
  item_code: string;
  part: CataloguePart | null;
  /** One part of a bundle the supplier priced as a whole: its prices are its
   * share of the bundle's, so the parts add up to what was quoted. */
  bundled: boolean;
}

export interface Issue {
  code: string;
  severity: Severity;
  message: string;
  line_seq: number | null;
  /** Whether the office may save in spite of it, giving a reason. Only the
   * totals check: the server refuses an override of anything else. */
  overridable: boolean;
}

export interface Candidate {
  inspection_id: number;
  work_order_no: string;
  site: string;
  suggested: boolean;
  /** Under the work order the quotation itself states. */
  by_work_order: boolean;
}

/** The City's work order the quotation states, and every site in the register
 * under it (numbers are not unique, so there can be several, or none). */
export interface WorkOrderRead {
  number: string;
  sites: { id: number; name: string; cluster: string | null }[];
}

/** What the quotation is saved against: an inspection already raised, or the
 * site it is for when no inspection exists yet (the server opens the job). */
export type JobTarget =
  | { kind: "inspection"; id: number }
  | { kind: "site"; id: number; name: string; workOrder: string | null };

export interface Review {
  supplier: { id: number; acc_no: string; name: string; discount_pct: string } | null;
  quotation: {
    read_as: string;
    number: string;
    your_ref: string;
    quote_date: string;
    valid_to: string;
    site: string;
    discount_pct: string;
    printed_subtotal: string | null;
    printed_vat: string | null;
    printed_total: string | null;
    totals_agree: boolean;
  };
  lines: ReadLine[];
  issues: Issue[];
  can_save: boolean;
  candidates: Candidate[];
  work_order: WorkOrderRead | null;
  catalogue: CataloguePart[];
}

export interface LineToSave {
  seq: number;
  kind: SavedKind;
  description: string;
  size: string;
  qty: string;
  quoted_unit_cost: string | null;
  quoted_unit_price: string | null;
  spare_part_id: number | null;
}

export interface SaveRequest {
  supplier_id: number;
  inspection_id: number | null;
  site_id: number | null;
  number: string;
  quote_date: string | null;
  valid_until: string | null;
  document_discount_pct: string;
  lines: LineToSave[];
  attachment_base64: string | null;
  /** Blocking checks saved over, and why -- kept on the quotation's history. */
  overridden: string[];
  override_reason: string | null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function refusal(response: Response): Promise<ApiError> {
  let detail = `${response.status} ${response.statusText}`;
  try {
    const body: unknown = await response.json();
    if (typeof body === "object" && body !== null && "detail" in body) {
      const found = (body as { detail: unknown }).detail;
      detail = typeof found === "string" ? found : JSON.stringify(found);
    }
  } catch {
    // The body was not JSON; the status line is what there is.
  }
  return new ApiError(detail, response.status);
}

export async function readPdf(url: string, file: File): Promise<Review> {
  const form = new FormData();
  form.append("document", file, file.name);
  const response = await fetch(url, { method: "POST", body: form, credentials: "same-origin" });
  if (!response.ok) {
    throw await refusal(response);
  }
  return (await response.json()) as Review;
}

export async function saveQuotation(
  url: string,
  request: SaveRequest,
): Promise<{ quotation_id: number; url: string }> {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
    credentials: "same-origin",
  });
  if (!response.ok) {
    throw await refusal(response);
  }
  return (await response.json()) as { quotation_id: number; url: string };
}

/** A file as base64, for the save request to carry the PDF beside its figures. */
export function fileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the file"));
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.readAsDataURL(file);
  });
}
